import { useRef, type PointerEvent } from 'react'
import type { ScratchArea, ScratchImage } from '@/lib/purchase-scratch'
import { areaFromPoints } from './scratch-area'
import styles from './PurchaseScratchCard.module.css'

export function ScratchAreaEditor({
  image,
  value,
  onChange,
}: {
  image: ScratchImage
  value: ScratchArea
  onChange: (area: ScratchArea) => void
}) {
  const start = useRef<{ x: number; y: number; pointer: number } | null>(null)
  function point(event: PointerEvent<HTMLDivElement>) {
    const rect = event.currentTarget.getBoundingClientRect()
    return {
      x: Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100)),
      y: Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100)),
    }
  }
  function move(event: PointerEvent<HTMLDivElement>) {
    if (start.current?.pointer === event.pointerId)
      onChange(areaFromPoints(start.current, point(event)))
  }
  return (
    <div className={styles.editor}>
      <div
        className={styles.selection}
        style={{ aspectRatio: `${image.width}/${image.height}` }}
        onPointerDown={(event) => {
          if (event.button !== 0 || start.current) return
          start.current = { ...point(event), pointer: event.pointerId }
          event.currentTarget.setPointerCapture(event.pointerId)
        }}
        onPointerMove={move}
        onPointerUp={(event) => {
          move(event)
          start.current = null
        }}
        onPointerCancel={() => {
          start.current = null
        }}
        onLostPointerCapture={() => {
          start.current = null
        }}
      >
        <img
          src={image.url}
          alt="Capa da raspadinha: arraste para selecionar a área raspável"
          draggable={false}
        />
        <div
          className={styles.region}
          style={{
            left: `${value.x}%`,
            top: `${value.y}%`,
            width: `${value.width}%`,
            height: `${value.height}%`,
          }}
        >
          <span>Área raspável</span>
          <i />
          <i />
          <i />
          <i />
        </div>
      </div>
      <p className={styles.hint}>
        Arraste sobre a capa para desenhar o retângulo. Use os campos abaixo para ajustar
        com precisão.
      </p>
      <div className={styles.coordinates}>
        {(['x', 'y', 'width', 'height'] as const).map((key) => (
          <label key={key}>
            {{ x: 'Esquerda', y: 'Topo', width: 'Largura', height: 'Altura' }[key]} (%)
            <input
              type="number"
              min={key === 'x' || key === 'y' ? 0 : 1}
              max={100}
              step="0.1"
              value={Number(value[key].toFixed(2))}
              onChange={(event) =>
                onChange({ ...value, [key]: Number(event.target.value) })
              }
            />
          </label>
        ))}
      </div>
      <button
        type="button"
        className={styles.quietButton}
        onClick={() => onChange({ x: 0, y: 0, width: 100, height: 100 })}
      >
        Raspar a imagem inteira
      </button>
    </div>
  )
}
